/*
 * Figma `125:8` — SA3-03 · Transactions.
 *
 * `TableTemplate`: cross-event search, the exception in the row, and the order's evidence in a
 * drawer. One row is one order across three systems — payment, slip verification and delivery —
 * which is the whole reason this screen exists: an organizer asks "where is my customer's ticket"
 * and the answer lives in whichever of the three went wrong.
 *
 * So `exceptionOf` names that, per row, from data already on the DTO. A row with nothing wrong says
 * nothing: a screen that labels every order is a screen nobody scans.
 *
 * The ticket-link reveal is the high-risk action here and it is treated as one. The link *is* a
 * capability — holding it opens somebody's ticket — so it goes through a consequence dialog rather
 * than a `window.confirm`, the revealed value lives in component state and never in the row, and it
 * is dropped the moment the query changes.
 */
import { useState } from 'react';
import {
  Alert, Button, Card, ConfirmDialog, Drawer, FilterChips, KpiCard, SearchField, Select,
  StatusBadge, TableFooter, TableTemplate,
} from '../..';
import type { BadgeTone, Column } from '../..';
import type { Destination } from '../../destinations';
import type { SuperadminOperationDto, TenantSummaryDto } from '../../../api';
import type { SuperOperationsState } from './useSuperOperations';
import { useSuperOperations } from './useSuperOperations';

const baht = (n: number) => `฿${Math.round(n).toLocaleString('th-TH')}`;
const when = (iso: string) => new Date(iso).toLocaleString('th-TH');

const STATUS_TONE: Record<string, BadgeTone> = {
  paid: 'success', awaiting_verification: 'warning', rejected: 'danger',
  pending_payment: 'neutral', expired: 'neutral', cancelled: 'neutral',
};

export interface OperationException { tone: BadgeTone; label: string; why: string }

/** Which of the three systems this order is stuck in, from what the row already carries. */
export function exceptionOf(row: SuperadminOperationDto): OperationException | null {
  if (row.slip && row.slip.status === 'failed') {
    return { tone: 'danger', label: 'ตรวจสลิปไม่ผ่าน', why: `ลองแล้ว ${row.slip.attemptCount} ครั้ง — ต้องมีคนดูสลิปเอง` };
  }
  const failedDeliveries = row.deliveries.filter((d) => d.status === 'failed');
  if (failedDeliveries.length > 0) {
    return {
      tone: 'danger',
      label: 'ส่งบัตรไม่สำเร็จ',
      why: `${failedDeliveries.map((d) => d.channel).join(', ')} ส่งไม่ผ่าน — ผู้ซื้อยังไม่ได้รับบัตร`,
    };
  }
  if (row.orderStatus === 'paid' && row.deliveries.length === 0) {
    return { tone: 'warning', label: 'ยังไม่ได้ส่งบัตร', why: 'ชำระแล้วแต่ไม่มีบันทึกการส่งเลย' };
  }
  if (row.orderStatus === 'awaiting_verification') {
    return { tone: 'warning', label: 'รอตรวจสลิป', why: 'ยังอยู่ในคิวตรวจของผู้จัด' };
  }
  return null;
}

export function TransactionsScreen({ destination, state, events }: {
  destination: Destination;
  state: SuperOperationsState;
  events: readonly TenantSummaryDto[];
}) {
  const {
    rows, loading, loadError, search, setSearch, eventFilter, setEventFilter,
    statusFilter, setStatusFilter, nextOffset, loadMore, loadingMore,
    revealedLinks, revealBusy, revealError, reveal,
  } = state;
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmReveal, setConfirmReveal] = useState<SuperadminOperationDto | null>(null);

  const open = openId ? rows.find((r) => r.orderId === openId) ?? null : null;
  const exceptions = rows.filter((r) => exceptionOf(r)?.tone === 'danger').length;

  const columns: Column<SuperadminOperationDto>[] = [
    {
      key: 'order',
      header: 'ออร์เดอร์',
      width: '190px',
      cell: (r) => (
        <span className="jt-cell-stack">
          <span className="jt-mono">{r.orderCode}</span>
          <span className="jt-cell-sub">{r.eventName || r.eventSlug}</span>
        </span>
      ),
    },
    {
      key: 'buyer',
      header: 'ผู้ซื้อ',
      cell: (r) => (
        <span className="jt-cell-stack">
          <span>{r.customerName}</span>
          <span className="jt-cell-sub jt-mono">{r.customerPhone}</span>
        </span>
      ),
    },
    { key: 'total', header: 'ยอด', align: 'end', width: '130px', cell: (r) => <span className="jt-mono">{baht(r.total)}</span> },
    {
      key: 'status',
      header: 'สถานะ',
      width: '230px',
      cell: (r) => {
        const ex = exceptionOf(r);
        return (
          <span className="jt-cell-stack">
            <span className="jt-inline-actions">
              <StatusBadge tone={STATUS_TONE[r.orderStatus] ?? 'neutral'}>{r.orderStatus}</StatusBadge>
              {ex && <StatusBadge tone={ex.tone}>{ex.label}</StatusBadge>}
            </span>
            {ex && <span className="jt-cell-sub">{ex.why}</span>}
          </span>
        );
      },
    },
    { key: 'activity', header: 'ความเคลื่อนไหวล่าสุด', width: '200px', cell: (r) => when(r.latestActivityAt) },
  ];

  return (
    <TableTemplate
      gate={{ destination, loading, error: loadError || null }}
      kpis={[
        <KpiCard key="rows" label="ธุรกรรมที่แสดง" value={rows.length.toLocaleString('th-TH')} meta={nextOffset !== null ? 'ยังมีต่อ' : 'ครบแล้ว'} />,
        <KpiCard key="ex" label="ที่ต้องมีคนดู" value={exceptions.toLocaleString('th-TH')} tone={exceptions > 0 ? 'danger' : undefined} />,
      ]}
      toolbar={
        <>
          <FilterChips
            chips={[
              { key: '', label: 'ทุกสถานะ' },
              { key: 'awaiting_verification', label: 'รอตรวจสลิป' },
              { key: 'paid', label: 'ชำระแล้ว' },
              { key: 'rejected', label: 'ไม่ผ่าน' },
            ]}
            active={statusFilter}
            onSelect={setStatusFilter}
            label="สถานะออร์เดอร์"
          />
          <Select
            label="อีเวนต์"
            value={eventFilter}
            options={[{ value: '', label: 'ทุกอีเวนต์' }, ...events.map((e) => ({ value: e.id, label: e.name || e.slug }))]}
            onChange={(e) => setEventFilter(e.target.value)}
          />
          <SearchField value={search} onChange={setSearch} label="ค้นหาธุรกรรม" placeholder="รหัสออร์เดอร์ / เบอร์ / อีเมล" />
        </>
      }
      table={{
        label: 'ธุรกรรมทุกอีเวนต์',
        columns,
        rows,
        rowKey: (r) => r.orderId,
        filtered: !!search.trim() || !!eventFilter || !!statusFilter,
        onClearFilters: () => { setSearch(''); setEventFilter(''); setStatusFilter(''); },
        emptyTitle: 'ไม่พบธุรกรรม',
        onRowClick: (r) => setOpenId(r.orderId),
        activeRowKey: openId,
        card: (r) => (
          <span className="jt-cell-stack">
            <span className="jt-mono">{r.orderCode}</span>
            <span className="jt-cell-sub">{r.customerName} · {baht(r.total)}</span>
            <StatusBadge tone={STATUS_TONE[r.orderStatus] ?? 'neutral'}>{r.orderStatus}</StatusBadge>
          </span>
        ),
        footer: (
          <TableFooter
            shown={rows.length}
            onLoadMore={nextOffset !== null ? loadMore : undefined}
            loadingMore={loadingMore}
          />
        ),
      }}
      drawer={
        <>
          <Drawer open={!!open} onClose={() => setOpenId(null)} title={open ? `ออร์เดอร์ ${open.orderCode}` : ''}>
            {open && (
              <>
                {exceptionOf(open) && (
                  <Alert tone={exceptionOf(open)!.tone === 'danger' ? 'danger' : 'warning'} title={exceptionOf(open)!.label}>
                    {exceptionOf(open)!.why}
                  </Alert>
                )}

                <Card title="ผู้ซื้อและอีเวนต์">
                  <ul className="jt-order-lines">
                    <li><span>อีเวนต์</span><span>{open.eventName || open.eventSlug}</span></li>
                    <li><span>ชื่อ</span><span>{open.customerName}</span></li>
                    <li><span>เบอร์</span><span className="jt-mono">{open.customerPhone}</span></li>
                    <li><span>อีเมล</span><span className="jt-mono">{open.customerEmail || '—'}</span></li>
                    <li className="jt-order-total"><span>ยอด</span><span className="jt-mono">{baht(open.total)}</span></li>
                  </ul>
                </Card>

                <Card title="การตรวจสลิป">
                  {open.slip ? (
                    <ul className="jt-order-lines">
                      <li><span>ผลล่าสุด</span><span>{open.slip.status}</span></li>
                      <li><span>ผู้ให้บริการ</span><span>{open.slip.vendor ?? '—'}</span></li>
                      <li><span>ลองไปแล้ว</span><span className="jt-mono">{open.slip.attemptCount} ครั้ง</span></li>
                    </ul>
                  ) : <p className="jt-field-hint">ออร์เดอร์นี้ไม่มีการตรวจสลิป</p>}
                </Card>

                <Card title="การส่งบัตร">
                  {open.deliveries.length === 0
                    ? <p className="jt-field-hint">ยังไม่มีบันทึกการส่ง</p>
                    : (
                      <ul className="jt-order-lines">
                        {open.deliveries.map((d, i) => (
                          <li key={`${d.channel}-${i}`}>
                            <span>{d.channel}</span>
                            <span>{d.status}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                </Card>

                <Card title="ลิงก์บัตรฉุกเฉิน" sub="ใช้เมื่อผู้ซื้อเข้าถึงบัตรของตัวเองไม่ได้จริง ๆ เท่านั้น">
                  {revealError && <Alert tone="danger" title="เปิดเผยไม่สำเร็จ">{revealError}</Alert>}
                  {!open.ticketLinkId ? (
                    <p className="jt-field-hint">ออร์เดอร์นี้ยังไม่มีลิงก์บัตร</p>
                  ) : revealedLinks[open.ticketLinkId] ? (
                    <>
                      <code className="jt-mono jt-credential-secret">{revealedLinks[open.ticketLinkId]}</code>
                      <Alert tone="warning" title="ลิงก์นี้เปิดบัตรของผู้ซื้อได้ทันที">
                        ส่งให้เจ้าของออร์เดอร์เท่านั้น การเปิดเผยครั้งนี้ถูกบันทึกใน Audit log แล้ว
                      </Alert>
                    </>
                  ) : (
                    <>
                      <p className="jt-field-hint">
                        {open.ticketTokenMasked ?? '••••'}
                        {open.ticketLinkExpiresAt && ` · หมดอายุ ${when(open.ticketLinkExpiresAt)}`}
                        {open.ticketLinkFirstOpenedAt && ` · เปิดครั้งแรก ${when(open.ticketLinkFirstOpenedAt)}`}
                      </p>
                      <div className="jt-inline-actions">
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={revealBusy === open.ticketLinkId}
                          onClick={() => setConfirmReveal(open)}
                        >
                          {revealBusy === open.ticketLinkId ? 'กำลังเปิดเผย…' : 'เปิดเผยลิงก์บัตร'}
                        </Button>
                      </div>
                    </>
                  )}
                </Card>
              </>
            )}
          </Drawer>

          <ConfirmDialog
            open={!!confirmReveal?.ticketLinkId}
            onClose={() => setConfirmReveal(null)}
            onConfirm={() => {
              const linkId = confirmReveal?.ticketLinkId;
              setConfirmReveal(null);
              if (linkId) void reveal(linkId);
            }}
            title={`เปิดเผยลิงก์บัตรของ ${confirmReveal?.orderCode ?? ''}`}
            confirmLabel="เปิดเผยลิงก์"
            consequences={[
              'ลิงก์นี้เปิดบัตรของผู้ซื้อได้ทันทีโดยไม่ต้องยืนยันตัวตนอีก',
              'การเปิดเผยครั้งนี้จะถูกบันทึกใน Audit log พร้อมชื่อผู้เปิด',
              'ส่งต่อให้เจ้าของออร์เดอร์เท่านั้น — ใครก็ตามที่ได้ลิงก์ไปใช้บัตรใบนี้ได้',
            ]}
          />
        </>
      }
    />
  );
}

export function TransactionsDestination({ token, destination, events }: {
  token: string;
  destination: Destination;
  events: readonly TenantSummaryDto[];
}) {
  const state = useSuperOperations(token);
  return <TransactionsScreen destination={destination} state={state} events={events} />;
}
